/**
 * Edit Question Screen
 */

import React, { useState, useCallback, useEffect } from "react";
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  TextInput,
  Alert,
  ActivityIndicator,
  KeyboardAvoidingView,
  Platform,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { Ionicons } from "@expo/vector-icons";
import { useRouter, useLocalSearchParams } from "expo-router";
import { useThemeColors } from "@/providers/ThemeProvider";
import { supabase } from "@/lib/supabase/client";
import { QuizQuestion, QuizCategory, QuestionType, QuestionDifficulty, QuizOption } from "@/modules/quiz";

const QUESTION_TYPES: { value: QuestionType; label: string; icon: string }[] = [
  { value: "multiple_choice", label: "Meerkeuze", icon: "list" },
  { value: "true_false", label: "Waar/Onwaar", icon: "swap-horizontal" },
  { value: "text_answer", label: "Open vraag", icon: "text" },
];

const DIFFICULTY_OPTIONS: { value: QuestionDifficulty; label: string; color: string }[] = [
  { value: "easy", label: "Makkelijk", color: "#10B981" },
  { value: "medium", label: "Gemiddeld", color: "#F59E0B" },
  { value: "hard", label: "Moeilijk", color: "#EF4444" },
];

export default function EditQuestionScreen() {
  const router = useRouter();
  const { id, questionId } = useLocalSearchParams<{ id: string; questionId: string }>();
  const colors = useThemeColors();

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [categories, setCategories] = useState<QuizCategory[]>([]);

  // Form
  const [question, setQuestion] = useState("");
  const [questionType, setQuestionType] = useState<QuestionType>("multiple_choice");
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [difficulty, setDifficulty] = useState<QuestionDifficulty>("medium");
  const [points, setPoints] = useState(10);
  const [timeLimit, setTimeLimit] = useState(30);
  const [explanation, setExplanation] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [options, setOptions] = useState<QuizOption[]>([]);
  const [correctAnswer, setCorrectAnswer] = useState("");

  const fetchData = useCallback(async () => {
    try {
      // Categories
      const { data: cats } = await supabase
        .from("quiz_categories")
        .select("*")
        .eq("is_active", true)
        .order("sort_order");
      if (cats) setCategories(cats as QuizCategory[]);

      // Question
      const { data, error } = await supabase
        .from("quiz_questions")
        .select("*")
        .eq("id", questionId)
        .single();

      if (error) {
        Alert.alert("Fout", "Kon vraag niet laden");
        router.back();
        return;
      }

      const q = data as QuizQuestion;
      setQuestion(q.question);
      setQuestionType(q.question_type);
      setCategoryId(q.category_id);
      setDifficulty(q.difficulty);
      setPoints(q.points);
      setTimeLimit(q.time_limit_seconds);
      setExplanation(q.explanation || "");
      setIsActive(q.is_active);
      setOptions(q.options.length > 0 ? q.options : [{ text: "", is_correct: true }, { text: "", is_correct: false }]);
      setCorrectAnswer(q.correct_answer || "");
    } finally {
      setIsLoading(false);
    }
  }, [questionId, router]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleOptionChange = (index: number, text: string) => {
    setOptions((prev) => prev.map((o, i) => (i === index ? { ...o, text } : o)));
  };

  const handleCorrectChange = (index: number) => {
    setOptions((prev) => prev.map((o, i) => ({ ...o, is_correct: i === index })));
  };

  const handleAddOption = () => {
    if (options.length < 6) {
      setOptions((prev) => [...prev, { text: "", is_correct: false }]);
    }
  };

  const handleRemoveOption = (index: number) => {
    if (options.length > 2) {
      const wasCorrect = options[index].is_correct;
      setOptions((prev) => {
        const newOpts = prev.filter((_, i) => i !== index);
        if (wasCorrect && newOpts.length > 0) newOpts[0].is_correct = true;
        return newOpts;
      });
    }
  };

  const setTrueFalse = (correctIsTrue: boolean) => {
    setOptions([
      { text: "Waar", is_correct: correctIsTrue },
      { text: "Onwaar", is_correct: !correctIsTrue },
    ]);
  };

  const validate = (): boolean => {
    if (!question.trim()) {
      Alert.alert("Fout", "Vul een vraag in");
      return false;
    }
    if (questionType !== "text_answer") {
      const filled = options.filter((o) => o.text.trim());
      if (filled.length < 2) {
        Alert.alert("Fout", "Vul minimaal 2 opties in");
        return false;
      }
      if (!options.some((o) => o.is_correct && o.text.trim())) {
        Alert.alert("Fout", "Selecteer een correct antwoord");
        return false;
      }
    } else if (!correctAnswer.trim()) {
      Alert.alert("Fout", "Vul het correcte antwoord in");
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setIsSaving(true);

    try {
      const finalOptions = questionType === "text_answer" ? [] : options.filter((o) => o.text.trim());

      const { error } = await supabase
        .from("quiz_questions")
        .update({
          question: question.trim(),
          question_type: questionType,
          category_id: categoryId,
          difficulty,
          points,
          time_limit_seconds: timeLimit,
          explanation: explanation.trim() || null,
          options: finalOptions,
          correct_answer: questionType === "text_answer" ? correctAnswer.trim() : null,
          is_active: isActive,
          updated_at: new Date().toISOString(),
        })
        .eq("id", questionId);

      if (error) throw error;

      router.back();
    } catch (error) {
      console.error(error);
      Alert.alert("Fout", "Kon vraag niet opslaan");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = () => {
    Alert.alert("Verwijderen", "Weet je zeker dat je deze vraag wilt verwijderen?", [
      { text: "Annuleren", style: "cancel" },
      {
        text: "Verwijderen",
        style: "destructive",
        onPress: async () => {
          await supabase.from("quiz_questions").delete().eq("id", questionId);
          router.back();
        },
      },
    ]);
  };

  if (isLoading) {
    return (
      <SafeAreaView className="flex-1 items-center justify-center" style={{ backgroundColor: colors.background }}>
        <ActivityIndicator size="large" color={colors.primary} />
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView className="flex-1" style={{ backgroundColor: colors.background }} edges={["top", "bottom"]}>
      <KeyboardAvoidingView behavior={Platform.OS === "ios" ? "padding" : undefined} className="flex-1">
        {/* Header */}
        <View className="px-4 py-4 flex-row items-center justify-between">
          <View className="flex-row items-center">
            <TouchableOpacity
              onPress={() => router.back()}
              className="w-10 h-10 rounded-full items-center justify-center mr-3"
              style={{ backgroundColor: colors.surface }}
            >
              <Ionicons name="arrow-back" size={22} color={colors.text} />
            </TouchableOpacity>
            <Text className="text-xl font-bold" style={{ color: colors.text }}>
              Vraag Bewerken
            </Text>
          </View>
          <View className="flex-row items-center">
            <TouchableOpacity
              onPress={handleDelete}
              className="w-10 h-10 rounded-full items-center justify-center mr-2"
              style={{ backgroundColor: colors.error + "20" }}
            >
              <Ionicons name="trash" size={18} color={colors.error} />
            </TouchableOpacity>
            <TouchableOpacity
              onPress={handleSave}
              disabled={isSaving}
              className="px-5 py-2.5 rounded-xl"
              style={{ backgroundColor: isSaving ? colors.border : colors.primary }}
            >
              {isSaving ? <ActivityIndicator size="small" color="#FFF" /> : <Text className="font-bold text-white">Opslaan</Text>}
            </TouchableOpacity>
          </View>
        </View>

        <ScrollView className="flex-1 px-4" keyboardShouldPersistTaps="handled">
          {/* Active */}
          <View className="rounded-xl p-4 mb-4 flex-row items-center justify-between" style={{ backgroundColor: colors.surface }}>
            <View className="flex-row items-center">
              <Ionicons name={isActive ? "eye" : "eye-off"} size={20} color={isActive ? "#10B981" : colors.error} />
              <Text className="ml-3" style={{ color: colors.text }}>Vraag actief</Text>
            </View>
            <TouchableOpacity
              onPress={() => setIsActive(!isActive)}
              className="w-14 h-8 rounded-full justify-center px-1"
              style={{ backgroundColor: isActive ? "#10B981" : colors.border }}
            >
              <View className="w-6 h-6 rounded-full bg-white" style={{ alignSelf: isActive ? "flex-end" : "flex-start" }} />
            </TouchableOpacity>
          </View>

          {/* Type */}
          <View className="mb-4">
            <Text className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>Type</Text>
            <View className="flex-row">
              {QUESTION_TYPES.map((t) => (
                <TouchableOpacity
                  key={t.value}
                  onPress={() => setQuestionType(t.value)}
                  className="flex-1 rounded-xl p-3 mr-2 items-center"
                  style={{
                    backgroundColor: questionType === t.value ? `${colors.primary}20` : colors.surface,
                    borderWidth: 2,
                    borderColor: questionType === t.value ? colors.primary : colors.border,
                  }}
                >
                  <Ionicons name={t.icon as any} size={20} color={questionType === t.value ? colors.primary : colors.textSecondary} />
                  <Text className="text-xs mt-1" style={{ color: questionType === t.value ? colors.primary : colors.textSecondary }}>{t.label}</Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>

          {/* Question */}
          <View className="mb-4">
            <Text className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>Vraag *</Text>
            <TextInput
              value={question}
              onChangeText={setQuestion}
              placeholder="Vraag..."
              placeholderTextColor={colors.textTertiary}
              multiline
              className="rounded-xl p-4"
              style={{ backgroundColor: colors.surface, color: colors.text, minHeight: 80, textAlignVertical: "top" }}
            />
          </View>

          {/* Category */}
          <View className="mb-4">
            <Text className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>Categorie</Text>
            <ScrollView horizontal showsHorizontalScrollIndicator={false}>
              <TouchableOpacity
                onPress={() => setCategoryId(null)}
                className="px-4 py-2 rounded-full mr-2"
                style={{ backgroundColor: categoryId === null ? colors.primary : colors.surface }}
              >
                <Text style={{ color: categoryId === null ? "#FFF" : colors.text }}>Geen</Text>
              </TouchableOpacity>
              {categories.map((c) => (
                <TouchableOpacity
                  key={c.id}
                  onPress={() => setCategoryId(c.id)}
                  className="px-4 py-2 rounded-full mr-2 flex-row items-center"
                  style={{ backgroundColor: categoryId === c.id ? c.color : colors.surface }}
                >
                  <Ionicons name={c.icon as any} size={14} color={categoryId === c.id ? "#FFF" : c.color} />
                  <Text className="ml-1" style={{ color: categoryId === c.id ? "#FFF" : colors.text }}>{c.name}</Text>
                </TouchableOpacity>
              ))}
            </ScrollView>
          </View>

          {/* Difficulty */}
          <View className="mb-4">
            <Text className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>Moeilijkheid</Text>
            <View className="flex-row">
              {DIFFICULTY_OPTIONS.map((d) => (
                <TouchableOpacity
                  key={d.value}
                  onPress={() => setDifficulty(d.value)}
                  className="flex-1 rounded-xl py-3 mr-2 items-center"
                  style={{ backgroundColor: difficulty === d.value ? d.color : `${d.color}20` }}
                >
                  <Text style={{ color: difficulty === d.value ? "#FFF" : d.color }}>{d.label}</Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>

          {/* Options - Multiple Choice */}
          {questionType === "multiple_choice" && (
            <View className="mb-4">
              <Text className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>Opties *</Text>
              {options.map((opt, i) => (
                <View key={i} className="flex-row items-center mb-2">
                  <TouchableOpacity
                    onPress={() => handleCorrectChange(i)}
                    className="w-10 h-10 rounded-full items-center justify-center mr-2"
                    style={{
                      backgroundColor: opt.is_correct ? "#10B98130" : colors.surface,
                      borderWidth: 2,
                      borderColor: opt.is_correct ? "#10B981" : colors.border,
                    }}
                  >
                    {opt.is_correct && <Ionicons name="checkmark" size={18} color="#10B981" />}
                  </TouchableOpacity>
                  <TextInput
                    value={opt.text}
                    onChangeText={(t) => handleOptionChange(i, t)}
                    placeholder={`Optie ${i + 1}`}
                    placeholderTextColor={colors.textTertiary}
                    className="flex-1 rounded-xl px-4 py-3"
                    style={{ backgroundColor: colors.surface, color: colors.text }}
                  />
                  {options.length > 2 && (
                    <TouchableOpacity
                      onPress={() => handleRemoveOption(i)}
                      className="w-10 h-10 rounded-full items-center justify-center ml-2"
                      style={{ backgroundColor: colors.error + "20" }}
                    >
                      <Ionicons name="remove" size={18} color={colors.error} />
                    </TouchableOpacity>
                  )}
                </View>
              ))}
              {options.length < 6 && (
                <TouchableOpacity
                  onPress={handleAddOption}
                  className="flex-row items-center justify-center py-3 rounded-xl"
                  style={{ backgroundColor: colors.surface, borderWidth: 1, borderColor: colors.border }}
                >
                  <Ionicons name="add" size={18} color={colors.primary} />
                  <Text className="ml-2" style={{ color: colors.primary }}>Optie toevoegen</Text>
                </TouchableOpacity>
              )}
            </View>
          )}

          {/* True/False */}
          {questionType === "true_false" && (
            <View className="mb-4">
              <Text className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>Correct antwoord</Text>
              <View className="flex-row">
                <TouchableOpacity
                  onPress={() => setTrueFalse(true)}
                  className="flex-1 rounded-xl py-4 mr-2 items-center"
                  style={{
                    backgroundColor: options[0]?.is_correct ? "#10B98130" : colors.surface,
                    borderWidth: 2,
                    borderColor: options[0]?.is_correct ? "#10B981" : colors.border,
                  }}
                >
                  <Ionicons name="checkmark-circle" size={28} color={options[0]?.is_correct ? "#10B981" : colors.textSecondary} />
                  <Text className="mt-2" style={{ color: options[0]?.is_correct ? "#10B981" : colors.text }}>Waar</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  onPress={() => setTrueFalse(false)}
                  className="flex-1 rounded-xl py-4 ml-2 items-center"
                  style={{
                    backgroundColor: options[1]?.is_correct ? "#EF444430" : colors.surface,
                    borderWidth: 2,
                    borderColor: options[1]?.is_correct ? "#EF4444" : colors.border,
                  }}
                >
                  <Ionicons name="close-circle" size={28} color={options[1]?.is_correct ? "#EF4444" : colors.textSecondary} />
                  <Text className="mt-2" style={{ color: options[1]?.is_correct ? "#EF4444" : colors.text }}>Onwaar</Text>
                </TouchableOpacity>
              </View>
            </View>
          )}

          {/* Text Answer */}
          {questionType === "text_answer" && (
            <View className="mb-4">
              <Text className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>Correct antwoord *</Text>
              <TextInput
                value={correctAnswer}
                onChangeText={setCorrectAnswer}
                placeholder="Het exacte antwoord"
                placeholderTextColor={colors.textTertiary}
                className="rounded-xl px-4 py-3"
                style={{ backgroundColor: colors.surface, color: colors.text }}
              />
            </View>
          )}

          {/* Explanation */}
          <View className="mb-4">
            <Text className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>Uitleg</Text>
            <TextInput
              value={explanation}
              onChangeText={setExplanation}
              placeholder="Extra uitleg"
              placeholderTextColor={colors.textTertiary}
              multiline
              className="rounded-xl p-4"
              style={{ backgroundColor: colors.surface, color: colors.text, minHeight: 60, textAlignVertical: "top" }}
            />
          </View>

          <View className="h-24" />
        </ScrollView>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}

